import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  CircularProgress,
  Pagination,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import AddIcon from "@mui/icons-material/Add";
import axios from "axios";
import dayjs from "dayjs";

import { getAuthCredentials } from "../../utils/auth";
import NotifHero from "./NotifHero";
import AddNotif from "./Modal/AddNotif";
import EditNotif from "./Modal/EditNotif";
import ViewNotif from "./Modal/ViewNotif";
import deleteNotification from "./Modal/DeleteNotif";

const NotifInfo = () => {
  const [notifications, setNotifications] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedNotif, setSelectedNotif] = useState(null);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const open = Boolean(anchorEl);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 1000);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    fetchNotifications(1, debouncedSearch);
  }, [debouncedSearch]);

  const fetchNotifications = async (targetPage = 1, keyword = "") => {
    setLoading(true);
    try {
      const { token } = await getAuthCredentials();
      const res = await axios.get(
        `${
          process.env.REACT_APP_BACKEND_URL
        }/v1/api/notification/all?page=${targetPage}&limit=10&search=${encodeURIComponent(
          keyword
        )}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const { notifications: notifData = [], totalPages = 1 } = res.data;
      setNotifications(notifData);
      setTotalPages(totalPages);
      setPage(targetPage);
    } catch (err) {
      console.error("Error fetching notifications:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
  };

  const handleMenuOpen = (event, notif) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedNotif(notif);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEdit = () => {
    handleMenuClose();
    setEditOpen(true);
  };

  const handleDelete = () => {
    handleMenuClose();
    if (selectedNotif) {
      deleteNotification(selectedNotif._id, selectedNotif.name, () =>
        fetchNotifications(page, debouncedSearch)
      );
    }
  };

  const handleRowClick = (notif) => {
    setSelectedNotif(notif);
    setViewOpen(true);
  };

  return (
    <Box sx={{ p: 3 }}>
      <NotifHero />

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
        mt={-2}
      >
        <Typography variant="h6">Notifications</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setAddOpen(true)}
        >
          Add Notif
        </Button>
      </Box>

      <TextField
        label="Search notifications"
        variant="outlined"
        fullWidth
        value={search}
        onChange={handleSearchChange}
        sx={{ mb: 3 }}
      />

      {loading ? (
        <Box display="flex" justifyContent="center" mt={5}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Paper elevation={2}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Users Viewed</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {notifications.map((notif) => (
                  <TableRow
                    key={notif._id}
                    hover
                    sx={{
                      cursor: "pointer",
                      "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.1)" },
                    }}
                    onClick={() => handleRowClick(notif)}
                  >
                    <TableCell>
                      {dayjs(notif.createdAt).format("YYYY-MM-DD HH:mm")}
                    </TableCell>
                    <TableCell>{notif.name}</TableCell>
                    <TableCell>
                      <div
                        dangerouslySetInnerHTML={{ __html: notif.description }}
                        style={{
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      />
                    </TableCell>
                    <TableCell>{notif.viewedBy?.length || 0}</TableCell>
                    <TableCell
                      align="right"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <IconButton onClick={(e) => handleMenuOpen(e, notif)}>
                        <MoreVertIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <Menu
              anchorEl={anchorEl}
              open={open}
              onClose={handleMenuClose}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
            >
              <MenuItem onClick={handleEdit}>Edit</MenuItem>
              <MenuItem onClick={handleDelete}>Delete</MenuItem>
            </Menu>
          </Paper>

          <Box display="flex" justifyContent="center" mt={3}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(_, value) =>
                fetchNotifications(value, debouncedSearch)
              }
              color="primary"
              shape="rounded"
            />
          </Box>
        </>
      )}

      <AddNotif
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onSuccess={() => fetchNotifications(page, debouncedSearch)}
      />
      <EditNotif
        open={editOpen}
        onClose={() => {
          setEditOpen(false);
          setSelectedNotif(null);
        }}
        notif={selectedNotif}
        onSuccess={() => fetchNotifications(page, debouncedSearch)}
      />
      <ViewNotif
        open={viewOpen}
        onClose={() => setViewOpen(false)}
        notif={selectedNotif}
      />
    </Box>
  );
};

export default NotifInfo;
